import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Callout, DataTable, type ColumnConfig } from '@/components/ui';
import { CreateAppRoleModal } from '../appRoles/CreateAppRoleModal';
import { appTileLabel } from './coverageHelpers';
import {
    AppNotApprovedEmptyState,
    NoAppSelectedEmptyState,
    NoRoleKeysEmptyState,
} from './EmptyStates';
import { RoleEditorChipsCell, RoleEditorIdentityCell } from './RoleEditorRowCells';
import { UndoBanner } from './UndoBanner';
import type { EditorRoleRow } from './types';
import type { RoleCoverageVM } from './useRoleCoverage';

/**
 * EAA-1817: the selected app's editor — header (identity, staleness line,
 * "Đồng bộ danh mục"/"Xem danh mục"), the "Vai trò chuẩn | Vai trò trong
 * {app}" table, the footer counts, and the undo banner for a just-removed
 * chip.
 *
 * Loads independently from `CoverageMatrixCard` (frame 54's loading note:
 * "Bảng phủ và panel tải riêng") — its own queries (`fetchRoleMaps`,
 * `fetchAppRoles`) are separate from the coverage query the matrix reads.
 */
export function RoleEditorCard({ vm }: { vm: RoleCoverageVM }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { board, editor, roleKeyCreate, canManage } = vm;
    const app = board.selectedAppRow;
    const tableContainerRef = useRef<HTMLDivElement>(null);
    // EAA-1841: "Tạo vai trò" target + the key of the role just created, which
    // this board deliberately cannot display (see the notice below).
    const [roleCreateOpen, setRoleCreateOpen] = useState(false);
    const [createdRoleKey, setCreatedRoleKey] = useState<string | null>(null);

    // EAA-1817: "Nhấp một ô để mở đúng app + vai trò đó" — a matrix click sets
    // `board.focusRequest`; once this app's rows are rendered, scroll the
    // matching role row into view and move focus onto it.
    //
    // EAA-1884: the table is now the kit `DataTable` (antd `Table` under the
    // hood), which has no `onRow` passthrough to attach a per-row ref/tabIndex
    // the way the old hand-rolled `<tr ref={rowRef} tabIndex={-1}>` did. Every
    // antd table row DOES carry its own `data-row-key` attribute (from
    // `rowKey="roleKey"` below), so scope a query to this table's container
    // instead of keeping a `Map<roleKey, HTMLTableRowElement>` built from a
    // per-row ref callback. `tabIndex` is set here, right before focusing,
    // rather than permanently on every row — functionally identical (it only
    // ever mattered at the moment of a programmatic `.focus()` call).
    useEffect(() => {
        if (!board.focusRequest) return;
        const container = tableContainerRef.current;
        if (!container) return;
        const row = container.querySelector<HTMLElement>(
            `[data-row-key="${CSS.escape(board.focusRequest.roleKey)}"]`
        );
        if (!row) return;
        row.tabIndex = -1;
        row.scrollIntoView({ block: 'nearest' });
        row.focus();
    }, [board.focusRequest, editor.editorRows]);

    // EAA-1884: destructured out of `editor` (rather than referenced as
    // `editor.x` inside the memo below) so the dep array can list these
    // individually instead of the whole `editor` object — react-hooks/
    // exhaustive-deps otherwise asks for the whole object once a callback
    // calls one of its methods (`editor.addRole(...)`), since a method call
    // can rely on more of the object than a plain property read proves.
    const {
        appRoleOptions,
        appRolesLoading,
        openPickerRoleKey,
        setOpenPickerRoleKey,
        addRole,
        removeChip,
        removePending,
    } = editor;

    // EAA-1884: kept above every early return — hooks must run unconditionally,
    // and `columns` is only ever read once `app` is non-null (below).
    const columns = useMemo<ColumnConfig<EditorRoleRow>[]>(
        () => [
            {
                key: 'standardRole',
                title: t('console.roleCoverage.editorCard.table.columnStandardRole'),
                render: (_value, row) => <RoleEditorIdentityCell row={row} />,
            },
            {
                key: 'appRole',
                title: t('console.roleCoverage.editorCard.table.columnAppRole', {
                    appName: app?.name ?? '',
                }),
                render: (_value, row) => (
                    <RoleEditorChipsCell
                        row={row}
                        appName={app?.name ?? ''}
                        canManage={canManage}
                        appRoleOptions={appRoleOptions}
                        appRolesLoading={appRolesLoading}
                        isPickerOpen={openPickerRoleKey === row.roleKey}
                        onOpenPicker={() => setOpenPickerRoleKey(row.roleKey)}
                        onClosePicker={() => setOpenPickerRoleKey(null)}
                        onAddRole={(appRole) => addRole(row.roleKey, appRole)}
                        onRemoveChip={(chip) => removeChip(row.roleKey, chip)}
                        removePending={removePending}
                    />
                ),
            },
        ],
        [
            t,
            app?.name,
            canManage,
            appRoleOptions,
            appRolesLoading,
            openPickerRoleKey,
            setOpenPickerRoleKey,
            addRole,
            removeChip,
            removePending,
        ]
    );

    if (!app) {
        return <NoAppSelectedEmptyState />;
    }

    if (!app.enabled) {
        return (
            <AppNotApprovedEmptyState
                appCode={app.code}
                appName={app.name}
                canManageApps={vm.canManage}
                onOpenApp={() => navigate('/console/apps')}
            />
        );
    }

    if (vm.roleKeyCount === 0) {
        return <NoRoleKeysEmptyState canManage={canManage} form={roleKeyCreate} />;
    }

    return (
        <section
            className="hz-card rc-editor-card"
            aria-label={t('console.roleCoverage.editorCard.sectionAriaLabel', {
                appName: app.name,
            })}
        >
            <div className="rc-editor-header">
                <span className="rc-app-tile" aria-hidden="true">
                    {appTileLabel(app.code)}
                </span>
                <div className="rc-editor-header-text">
                    <h2 className="rc-card-title">{app.name}</h2>
                    <p className="portal-admin-error">
                        {t('console.roleCoverage.editorCard.catalogSyncLine', {
                            ageLabel: editor.catalogAgeLabel,
                        })}
                        {editor.staleCount > 0 &&
                            t('console.roleCoverage.editorCard.staleNote', {
                                count: editor.staleCount,
                                appName: app.name,
                            })}
                    </p>
                </div>
                <span className="rc-editor-header-actions">
                    {/* EAA-1841: hidden unless the app opted in — the server
                        refuses the call for every other app, so a visible
                        button would only ever produce an error here. */}
                    {canManage && app.supportsRoleManagement && (
                        <Button className="portal-btn" onClick={() => setRoleCreateOpen(true)}>
                            {t('console.roleCoverage.editorCard.createRoleButton')}
                        </Button>
                    )}
                    {editor.canSync && (
                        <Button
                            className="portal-btn"
                            disabled={editor.syncMutation.isPending}
                            onClick={() => editor.syncMutation.mutate()}
                        >
                            {editor.syncMutation.isPending
                                ? t('console.roleCoverage.editorCard.syncPending')
                                : t('console.roleCoverage.editorCard.syncButton')}
                        </Button>
                    )}
                    <Button
                        className="portal-btn"
                        disabled={!editor.canOpenCatalog}
                        title={
                            editor.canOpenCatalog
                                ? undefined
                                : t('console.roleCoverage.editorCard.viewCatalogDisabledTitle')
                        }
                        aria-expanded={editor.catalogViewerOpen}
                        onClick={() => editor.setCatalogViewerOpen((open) => !open)}
                    >
                        {t('console.roleCoverage.editorCard.viewCatalogButton', {
                            count: app.catalogRoleCount,
                        })}
                    </Button>
                </span>
            </div>

            {editor.catalogViewerOpen && editor.canOpenCatalog && (
                <ul className="rc-catalog-viewer">
                    {editor.appRolesLoading && (
                        <li className="portal-admin-hint">
                            {t('console.roleCoverage.editorCard.loadingCatalogViewer')}
                        </li>
                    )}
                    {!editor.appRolesLoading && editor.appRoleOptions.length === 0 && (
                        <li className="portal-admin-hint">
                            {t('console.roleCoverage.editorCard.catalogEmpty')}
                        </li>
                    )}
                    {editor.appRoleOptions.map((option) => (
                        <li key={option.key} className="rc-catalog-viewer-row">
                            <span className="rc-role-key">{option.key}</span>
                            <span>{option.name}</span>
                        </li>
                    ))}
                </ul>
            )}

            {/* EAA-1841: a role created here is ORG-SCOPED server-side, and this
                board reads ecosystem-default links ONLY (see
                `useRoleCoverageData`) — so it genuinely will not appear in the
                matrix or the catalog viewer above. Say where it did land rather
                than letting the operator read the absence as a failure. */}
            {createdRoleKey && (
                <Callout
                    tone="info"
                    label={t('console.roleCoverage.roleCreatedCallout.label')}
                    longLabel
                >
                    <Trans
                        i18nKey="console.roleCoverage.roleCreatedCallout.message"
                        values={{ roleKey: createdRoleKey, appName: app.name }}
                        components={{
                            code: <code />,
                            strong: <strong />,
                            link: <Link to="/console/role-map" />,
                        }}
                    />
                </Callout>
            )}

            {editor.writeError && <p className="portal-admin-error">{editor.writeError}</p>}

            <div className="portal-table-scroll" ref={tableContainerRef}>
                <DataTable<EditorRoleRow>
                    className="rc-role-editor-table"
                    data={editor.editorRows}
                    columns={columns}
                    rowKey="roleKey"
                    pagination={false}
                    rowClassName="rc-role-row"
                    // EAA-1884: WHY empty — the default `scroll={{x:900}}` makes
                    // antd apply `overflow-y: hidden` on the scroll wrapper
                    // whenever `scroll.x` is set (see `@rc-component/table`'s
                    // `Table.js`), which would clip `RolePickerPopover` (an
                    // absolutely-positioned popover that intentionally extends
                    // below its row, not a portal-rendered overlay). This table
                    // only ever has two columns and never needed horizontal
                    // scroll.
                    scroll={{}}
                />
            </div>

            <div className="rc-editor-footer">
                <span className="portal-admin-hint">
                    {t('console.roleCoverage.editorCard.footer.summary', {
                        roleKeyCount: vm.roleKeyCount,
                        totalLinks: editor.totalLinks,
                        appName: app.name,
                    })}
                </span>
                <span className="portal-admin-hint">
                    {t('console.roleCoverage.editorCard.footer.hint')}
                </span>
            </div>

            <UndoBanner items={editor.pendingUndos} onUndo={editor.undoRemove} />

            <CreateAppRoleModal
                app={roleCreateOpen ? { code: app.code, name: app.name } : null}
                onClose={() => setRoleCreateOpen(false)}
                onCreated={(role) => setCreatedRoleKey(role.key)}
            />
        </section>
    );
}

export default RoleEditorCard;
